import React, { useState, useEffect, memo, useRef } from 'react';
import HeaderCell from './HeaderCell';
import SpanHeaderCell from './SpanHeaderCell';
import FavouriteHeaderCell from './FavouriteHeaderCell';
import ScrollbarRow from './ScrollbarRow';
import SelectHeaderCell from './SelectHeaderCell';

const calcStickyCols = (definitions) => {
    let retVal = 0;

    definitions.forEach(({ isSticky, type }) => {
        switch (type) {
            case 'action':
            case 'favourite':
                retVal++;
                break;

            default:
                if (isSticky) retVal++;
                break;
        }
    });

    return retVal;
};

const HeaderRow = memo(
    ({
        definitions,
        onSortOrderChange,
        headerRowHeight,
        scrollPxH,
        onScroll,
        tableWidth,
        tableOffsetH,
        containerWidth,
        addGutterCap = false,
        areAllSelected = false,
        onTick,
        actionIconSize
    }) => {
        const tableHeadRef = useRef(null);
        const scrollBarRef = useRef(null);
        const rowRef = useRef(null);

        const [stickyColCount, setStickyColCount] = useState(0);
        const [scrollPxV, setScrollPxV] = useState(0);

        const actionHeaderSpan = definitions.reduce((acc, val) => {
            return val.type === 'action' ? acc + 1 : acc;
        }, 0);

        const handleScroll = () => {
            if (!rowRef.current) return;
            let top = 0;
            if (!scrollBarRef.current) {
                top = rowRef.current.getBoundingClientRect().top;
            } else {
                top = scrollBarRef.current.getBoundingClientRect().top;
            }
            setScrollPxV(top <= 0 ? top : 0);
        };

        useEffect(() => {
            setStickyColCount(calcStickyCols(definitions));

            window.addEventListener('scroll', handleScroll);
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }, []);

        return (
            <thead ref={tableHeadRef}>
                {tableWidth > containerWidth + 3 && (
                    <ScrollbarRow
                        ref={scrollBarRef}
                        scrollPxH={scrollPxH}
                        onScroll={onScroll}
                        colCount={definitions.length}
                        tableWidth={tableWidth}
                        tableOffset={tableOffsetH}
                        containerWidth={containerWidth}
                        stickyColCount={stickyColCount}
                        scrollPxV={scrollPxV}
                    />
                )}

                <tr ref={rowRef}>
                    {definitions.map((d, i) => {
                        let cellComponent = null;

                        switch (d.type) {
                            case 'action':
                                cellComponent =
                                    d.headerName !== '' ? (
                                        <SpanHeaderCell
                                            key={`header${i}`}
                                            headerName={d.headerName}
                                            colSpan={actionHeaderSpan}
                                            width={d.width}
                                            scrollPxH={scrollPxH}
                                            scrollPxV={scrollPxV}
                                            isColSticky
                                            style={{
                                                height: headerRowHeight
                                            }}
                                            {...d.props}
                                        />
                                    ) : null;
                                break;

                            case 'favourite':
                                cellComponent = (
                                    <FavouriteHeaderCell
                                        key={`header${i}`}
                                        def={d}
                                        onSortOrderChange={onSortOrderChange}
                                        scrollPxH={scrollPxH}
                                        scrollPxV={scrollPxV}
                                        actionIconSize={actionIconSize}
                                        style={{
                                            height: headerRowHeight
                                        }}
                                    />
                                );
                                break;

                            case 'select':
                                cellComponent = (
                                    <SelectHeaderCell
                                        key={`header${i}`}
                                        headerName={d.headerName}
                                        disabled={d.disabled}
                                        gap={d.tickboxGap}
                                        size={d.tickboxSize}
                                        style={{
                                            height: headerRowHeight
                                        }}
                                        reverseElements={d.reverseElements}
                                        tickboxStyle={d.tickboxStyle}
                                        tickboxContainerStyle={
                                            d.tickboxContainerStyle
                                        }
                                        isColSticky={d.isSticky}
                                        areAllSelected={areAllSelected}
                                        onTick={onTick}
                                        scrollPxH={scrollPxH}
                                        scrollPxV={scrollPxV}
                                        colSpan={
                                            addGutterCap &&
                                            d ===
                                                definitions[
                                                    definitions.length - 1
                                                ]
                                                ? 2
                                                : 1
                                        }
                                    />
                                );
                                break;

                            default:
                                // If this is the last cell in the header, and we have a scrollbar, then this cell will be stretched to cover act as a gutter cap.
                                cellComponent = (
                                    <HeaderCell
                                        key={`header${i}`}
                                        def={{ ...d, height: headerRowHeight }}
                                        onSortOrderChange={onSortOrderChange}
                                        zIndexMod={i}
                                        width={d.width}
                                        scrollPxH={scrollPxH}
                                        scrollPxV={scrollPxV}
                                        style={
                                            addGutterCap &&
                                            d ===
                                                definitions[
                                                    definitions.length - 1
                                                ]
                                                ? { paddingRight: 16 }
                                                : {}
                                        }
                                        colSpan={
                                            addGutterCap &&
                                            d ===
                                                definitions[
                                                    definitions.length - 1
                                                ]
                                                ? 2
                                                : 1
                                        }
                                    />
                                );
                        }

                        return cellComponent;
                    })}
                </tr>
            </thead>
        );
    }
);

HeaderRow.displayName = 'HeaderRow';

export default HeaderRow;
